// client/src/coms/components/serversettings_parts/ServerSoundboardManager.js
// Server settings → Soundboard (also opened from the voice soundboard for members who may upload).
//   Library: the server's shared sounds (25 slots across all of its packs), MP3 upload by picker or drag and
//            drop with progress, optional trimming, name / emoji / description / keywords, preview, reorder,
//            replace and delete. Uploaders edit the sounds they added; Manage server sounds edits all.
//   Packs:   how the server's packs may be shared (only here / members elsewhere / anyone with the code /
//            Marketplace), and shared packs enabled for this server.
//   Rules:   the server's soundboard policy and per-channel pauses (Manage soundboard settings).
//   Roles:   the soundboard permissions of each role, edited through the normal role permission API.
// Every action is checked by the server; this screen only hides what the user cannot do.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { comsApiDelete, comsApiGet, comsApiPatch, comsApiPost, comsApiPut } from '../../comsApi';
import { inspectSoundFile, nameFromFile, uploadWithProgress } from '../../soundboard/soundUpload';
import { previewSound, stopPreview } from '../../soundboard/soundboardEngine';
import { Waveform } from '../SoundClipEditor';
import '../../soundboard/soundboard.css';

const SB_PERMS = [
  ['use_soundboard', 'Use soundboard'],
  ['use_external_sounds', 'Use external sounds'],
  ['upload_server_sounds', 'Upload server sounds'],
  ['manage_soundboard', 'Manage server sounds'],
  ['manage_soundboard_settings', 'Manage soundboard settings'],
  ['bypass_soundboard_cooldown', 'Bypass cooldown'],
];

const SHARE = [
  ['local', 'Only in this server'],
  ['members', 'Our members may also use them in other servers'],
  ['anyone', 'Anyone with the share code'],
];

function errText(e, fallback) {
  return String((e && e.data && e.data.error) || (e && e.message) || fallback);
}

function secs(ms) {
  return `${(Math.max(0, Number(ms) || 0) / 1000).toFixed(1)} s`;
}

let uploadSeq = 0;

export default function ServerSoundboardManager({ serverKey }) {
  const [lib, setLib] = useState(null);
  const [settings, setSettings] = useState(null);
  const [roles, setRoles] = useState(null);
  const [err, setErr] = useState('');
  const [notice, setNotice] = useState('');
  const [queue, setQueue] = useState([]);
  const [over, setOver] = useState(false);
  const [editing, setEditing] = useState(null); // { id, name, emoji, description, keywords }
  const [confirm, setConfirm] = useState('');
  const [previewing, setPreviewing] = useState(0);
  const [packCode, setPackCode] = useState('');
  const [newPack, setNewPack] = useState('');
  const [busy, setBusy] = useState(false);
  const [target, setTarget] = useState('');
  const fileRef = useRef(null);
  const replaceRef = useRef(null);
  const replacing = useRef(0);

  const key = encodeURIComponent(serverKey || '');

  const load = useCallback(async () => {
    if (!serverKey) return;
    try {
      const [l, s] = await Promise.all([comsApiGet(`/sounds/servers/${key}/library`), comsApiGet(`/sounds/server-settings/${key}`)]);
      setLib(l);
      setSettings(s);
      setErr('');
    } catch (e) {
      setErr(errText(e, 'Could not load the server’s soundboard.'));
    }
    try {
      setRoles(await comsApiGet(`/servers/${key}/roles`));
    } catch {
      setRoles(null);
    }
  }, [serverKey, key]);

  useEffect(() => {
    load();
    return () => stopPreview();
  }, [load]);

  useEffect(() => {
    if (!notice) return undefined;
    const t = setTimeout(() => setNotice(''), 3000);
    return () => clearTimeout(t);
  }, [notice]);

  const can = (lib && lib.can) || {};
  const me = lib && lib.me;
  const quota = (lib && lib.quota) || { used: 0, limit: 25 };
  const collections = useMemo(() => (lib && lib.collections) || [], [lib]);
  const free = Math.max(0, quota.limit - quota.used);
  const pending = queue.filter((q) => q.status !== 'done').length;
  const canEditSound = (s) => can.manage || (can.upload && s.created_by === me);

  async function act(fn, fallback, okText) {
    setBusy(true);
    setErr('');
    try {
      await fn();
      if (okText) setNotice(okText);
      await load();
    } catch (e) {
      setErr(errText(e, fallback));
    } finally {
      setBusy(false);
      setConfirm('');
    }
  }

  // ---- uploads ----
  async function addFiles(files) {
    const list = Array.from(files || []).slice(0, 25);
    for (const file of list) {
      const id = (uploadSeq += 1);
      setQueue((q) => [...q, { id, file, name: nameFromFile(file), emoji: '', description: '', keywords: '', status: 'checking', progress: 0, error: '' }]);
      const info = await inspectSoundFile(file);
      setQueue((q) =>
        q.map((it) =>
          it.id !== id
            ? it
            : info.ok
              ? { ...it, status: info.needsTrim ? 'trim' : 'ready', duration: info.duration, decoded: info.decoded, start: 0, end: Math.min(info.duration, 10) }
              : { ...it, status: 'error', error: info.error }
        )
      );
    }
  }

  const patchItem = (id, patch) => setQueue((q) => q.map((it) => (it.id === id ? { ...it, ...patch } : it)));

  async function upload(it) {
    patchItem(it.id, { status: 'uploading', progress: 0, error: '' });
    const fields = { name: it.name.trim() || 'Sound', emoji: it.emoji, description: it.description, keywords: it.keywords, collection_id: target || '' };
    if (it.decoded && (it.start > 0.01 || it.end < it.duration - 0.01)) {
      fields.trim_start_ms = Math.round(it.start * 1000);
      fields.trim_end_ms = Math.round(it.end * 1000);
    }
    const r = await uploadWithProgress(`/sounds/servers/${key}/sounds`, fields, it.file, (p) => patchItem(it.id, { progress: p }));
    if (r.status === 200) {
      patchItem(it.id, { status: 'done', progress: 1 });
      setNotice(`Added “${fields.name}”.`);
      setTimeout(() => setQueue((q) => q.filter((x) => x.id !== it.id)), 1200);
      load();
    } else {
      patchItem(it.id, { status: it.end - it.start > 10 ? 'trim' : 'ready', error: (r.data && r.data.error) || `Upload failed (${r.status})` });
    }
  }

  async function uploadAll() {
    for (const it of queue.filter((x) => x.status === 'ready')) await upload(it);
  }

  function onDrop(e) {
    e.preventDefault();
    setOver(false);
    if (!can.upload || free <= 0) return;
    addFiles(e.dataTransfer && e.dataTransfer.files);
  }

  async function replaceWith(file) {
    const id = replacing.current;
    replacing.current = 0;
    if (!file || !id) return;
    const info = await inspectSoundFile(file);
    if (!info.ok) return setErr(info.error);
    if (info.needsTrim) return setErr('That file is longer than 10 seconds. Upload it as a new sound to trim it, then delete the old one.');
    const r = await uploadWithProgress(`/sounds/sounds/${id}/audio`, {}, file);
    if (r.status !== 200) return setErr((r.data && r.data.error) || 'Could not replace the audio.');
    setNotice('Audio replaced.');
    load();
  }

  async function preview(s) {
    if (previewing === s.id) {
      stopPreview();
      setPreviewing(0);
      return;
    }
    setPreviewing(s.id);
    try {
      await previewSound({ hash: s.hash, url: s.url, volume: s.volume });
    } catch (e) {
      setErr(errText(e, 'Could not play that sound.'));
    } finally {
      setPreviewing((cur) => (cur === s.id ? 0 : cur));
    }
  }

  const move = (c, index, dir) => {
    const ids = c.sounds.map((s) => s.id);
    const j = index + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[index], ids[j]] = [ids[j], ids[index]];
    act(() => comsApiPut(`/sounds/collections/${c.id}/order`, { sound_ids: ids }), 'Could not reorder.');
  };

  const saveSound = () =>
    act(
      async () => {
        await comsApiPatch(`/sounds/sounds/${editing.id}`, { name: editing.name, emoji: editing.emoji, description: editing.description, keywords: editing.keywords });
        setEditing(null);
      },
      'Could not save the sound.',
      'Saved.'
    );

  // ---- policy ----
  const policy = settings && settings.policy;
  const canSettings = !!(settings && settings.can && settings.can.manage_settings);
  const canStop = !!(settings && settings.can && settings.can.stop);
  const numTimers = useRef({});

  function setPolicy(patch) {
    setSettings((s) => (s ? { ...s, ...(patch.enabled != null ? { enabled: patch.enabled } : {}), policy: { ...s.policy, ...patch } } : s));
    return comsApiPut(`/sounds/server-settings/${key}`, patch).catch((e) => {
      setErr(errText(e, 'Could not save the setting.'));
      load();
    });
  }

  function setPolicyNumber(k, v) {
    setSettings((s) => (s ? { ...s, policy: { ...s.policy, [k]: v } } : s));
    clearTimeout(numTimers.current[k]);
    numTimers.current[k] = setTimeout(() => setPolicy({ [k]: v }), 500);
  }

  const toggle = (k, label, hint, invert = false) => (
    <label className="vox-ssb-toggle" key={k}>
      <input type="checkbox" disabled={!canSettings} checked={invert ? !policy[k] : !!policy[k]} onChange={(e) => setPolicy({ [k]: invert ? !e.target.checked : e.target.checked })} />
      <span>
        {label}
        {hint ? <small>{hint}</small> : null}
      </span>
    </label>
  );

  // ---- roles ----
  const roleRows = useMemo(() => ((roles && roles.roles) || []).filter((r) => !r.is_owner), [roles]);
  const grantable = useMemo(() => new Set((roles && roles.me && roles.me.grantable) || []), [roles]);
  const canRoles = !!(roles && roles.me && roles.me.can_manage_roles);

  async function setRolePerm(role, k, v) {
    setRoles((r) => ({ ...r, roles: r.roles.map((x) => (x.id === role.id ? { ...x, [k]: v ? 1 : 0 } : x)) }));
    try {
      await comsApiPut(`/roles/${role.id}/permissions`, { [k]: v ? 1 : 0 });
    } catch (e) {
      setErr(errText(e, 'Could not change the role.'));
      load();
    }
  }

  if (!serverKey) return null;
  if (!lib && !err) return <div className="vox-ssb-sub">Loading the soundboard…</div>;

  const pct = quota.limit ? Math.min(100, Math.round((quota.used / quota.limit) * 100)) : 0;

  return (
    <div className="vox-ssb" data-testid="server-soundboard">
      {err ? (
        <div className="coms-form-error" role="alert">
          {err}
        </div>
      ) : null}
      {notice ? (
        <div className="vox-sbm-notice" role="status">
          {notice}
        </div>
      ) : null}

      {/* ---------------- library ---------------- */}
      <section className="vox-ssb-card" aria-labelledby="ssb-lib">
        <h3 id="ssb-lib">Server sounds</h3>
        <div className="vox-ssb-sub">Sounds everyone in {lib && lib.server ? lib.server.name : 'this server'} can play in voice channels. MP3, up to 1 MB and 10 seconds; loudness is evened out automatically.</div>
        <div className="vox-ssb-quota" data-testid="ssb-quota">
          <span>
            {quota.used} / {quota.limit} sound slots used
          </span>
          <span className={`vox-ssb-bar${free <= 0 ? ' full' : ''}`} aria-hidden="true">
            <span style={{ width: `${pct}%` }} />
          </span>
        </div>

        {can.upload ? (
          <>
            <div
              className={`vox-ssb-drop${over ? ' over' : ''}${free <= 0 ? ' disabled' : ''}`}
              role="button"
              tabIndex={0}
              aria-disabled={free <= 0}
              onClick={() => free > 0 && fileRef.current && fileRef.current.click()}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && free > 0 && fileRef.current && fileRef.current.click()}
              onDragOver={(e) => {
                e.preventDefault();
                if (free > 0) setOver(true);
              }}
              onDragLeave={() => setOver(false)}
              onDrop={onDrop}
              data-testid="ssb-drop"
            >
              {free > 0 ? (
                <>
                  <b>Drop MP3 files here</b> or click to choose · {free} slot{free === 1 ? '' : 's'} left
                </>
              ) : (
                'All sound slots are in use. Remove a sound to add another.'
              )}
              <input ref={fileRef} type="file" accept=".mp3,audio/mpeg" multiple style={{ display: 'none' }} data-testid="ssb-file" onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
            </div>
            {collections.length > 1 ? (
              <label style={{ fontSize: 12, display: 'flex', gap: 6, alignItems: 'center', marginTop: 8 }}>
                Add to
                <select className="vox-ssb-field" value={target} onChange={(e) => setTarget(e.target.value)}>
                  <option value="">{collections[0].name}</option>
                  {collections.slice(1).map((c) => (
                    <option key={c.id} value={String(c.id)}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
          </>
        ) : (
          <div className="vox-ssb-sub" style={{ marginTop: 8 }}>
            You can listen to and preview these sounds. Uploading needs the Upload server sounds permission.
          </div>
        )}

        {queue.length ? (
          <div style={{ marginTop: 10 }}>
            {queue.map((it) => (
              <div key={it.id} className="vox-ssb-row" data-testid="ssb-queue-item" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                  <input className="vox-ssb-field" value={it.emoji} onChange={(e) => patchItem(it.id, { emoji: e.target.value.slice(0, 4) })} placeholder="🔔" aria-label="Emoji" style={{ width: 46, textAlign: 'center' }} />
                  <input className="vox-ssb-field" value={it.name} onChange={(e) => patchItem(it.id, { name: e.target.value.slice(0, 40) })} aria-label="Sound name" style={{ flex: '1 1 140px' }} />
                  <input className="vox-ssb-field" value={it.keywords} onChange={(e) => patchItem(it.id, { keywords: e.target.value })} placeholder="Keywords" aria-label="Search keywords" style={{ flex: '1 1 120px' }} />
                  <span style={{ fontSize: 11, opacity: 0.65 }}>{it.duration ? secs(it.duration * 1000) : ''}</span>
                  {it.status === 'ready' || it.status === 'trim' ? (
                    <button type="button" className="coms-mini-btn tiny primary" disabled={it.end - it.start > 10.05} onClick={() => upload(it)}>
                      Upload
                    </button>
                  ) : null}
                  {it.status !== 'uploading' ? (
                    <button type="button" className="coms-mini-btn tiny" onClick={() => setQueue((q) => q.filter((x) => x.id !== it.id))} aria-label={`Discard ${it.name}`}>
                      ✕
                    </button>
                  ) : null}
                </div>
                <input className="vox-ssb-field" value={it.description} onChange={(e) => patchItem(it.id, { description: e.target.value.slice(0, 200) })} placeholder="Description (optional)" aria-label="Description" style={{ marginTop: 6 }} />
                {it.decoded && (it.status === 'trim' || it.duration > 10.05 || it.trimOpen) ? (
                  <div style={{ marginTop: 6 }}>
                    <Waveform samples={it.decoded.samples} rate={it.decoded.rate} start={it.start} end={it.end} />
                    <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 6, alignItems: 'center', fontSize: 12 }}>
                      <span>Start</span>
                      <input type="range" min={0} max={it.duration} step={0.01} value={it.start} aria-label="Trim start" onChange={(e) => patchItem(it.id, { start: Math.min(Number(e.target.value), it.end - 0.1) })} />
                      <span>{it.start.toFixed(2)} s</span>
                      <span>End</span>
                      <input type="range" min={0} max={it.duration} step={0.01} value={it.end} aria-label="Trim end" onChange={(e) => patchItem(it.id, { end: Math.max(Number(e.target.value), it.start + 0.1) })} />
                      <span>{it.end.toFixed(2)} s</span>
                    </div>
                    <div style={{ fontSize: 12, color: it.end - it.start > 10.05 ? '#ff8a8a' : 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.7 * var(--vx-ov, 1)))' }}>Clip length {(it.end - it.start).toFixed(2)} s (max 10 s)</div>
                  </div>
                ) : it.decoded && it.status === 'ready' ? (
                  <button type="button" className="coms-mini-btn tiny" style={{ alignSelf: 'flex-start', marginTop: 6 }} onClick={() => patchItem(it.id, { trimOpen: true })}>
                    Trim…
                  </button>
                ) : null}
                {it.status === 'checking' ? <div className="vox-ssb-sub">Checking the file…</div> : null}
                {it.status === 'uploading' || it.status === 'done' ? (
                  <div className="vox-ssb-progress" aria-label="Upload progress">
                    <span style={{ width: `${Math.round((it.progress || 0) * 100)}%` }} />
                  </div>
                ) : null}
                {it.status === 'uploading' && it.progress >= 1 ? <div className="vox-ssb-sub">Processing on the server…</div> : null}
                {it.error ? (
                  <div className="coms-form-error" role="alert" style={{ marginTop: 6 }}>
                    {it.error}
                  </div>
                ) : null}
              </div>
            ))}
            {queue.filter((x) => x.status === 'ready').length > 1 ? (
              <button type="button" className="coms-mini-btn primary" style={{ marginTop: 8 }} onClick={uploadAll} disabled={busy || !pending}>
                Upload all
              </button>
            ) : null}
          </div>
        ) : null}

        <input ref={replaceRef} type="file" accept=".mp3,audio/mpeg" style={{ display: 'none' }} onChange={(e) => { replaceWith(e.target.files && e.target.files[0]); e.target.value = ''; }} />

        {collections.map((c) => (
          <div key={c.id} style={{ marginTop: 14 }}>
            {collections.length > 1 ? <div className="vox-sb-group-title">{c.name}</div> : null}
            {c.sounds.length ? (
              c.sounds.map((s, i) => {
                const mine = canEditSound(s);
                const isEditing = editing && editing.id === s.id;
                return (
                  <div key={s.id} className="vox-ssb-row" data-testid="ssb-sound">
                    <button type="button" className="vox-sbm-play" onClick={() => preview(s)} aria-label={previewing === s.id ? `Stop ${s.name}` : `Preview ${s.name}`} title="Preview (only you hear it)">
                      {previewing === s.id ? '■' : '▶'}
                    </button>
                    {isEditing ? (
                      <div className="main" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          <input className="vox-ssb-field" value={editing.emoji} onChange={(e) => setEditing({ ...editing, emoji: e.target.value.slice(0, 4) })} aria-label="Emoji" style={{ width: 46, textAlign: 'center' }} />
                          <input className="vox-ssb-field" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value.slice(0, 40) })} aria-label="Sound name" style={{ flex: 1, minWidth: 120 }} autoFocus />
                        </div>
                        <input className="vox-ssb-field" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value.slice(0, 200) })} placeholder="Description" aria-label="Description" />
                        <input className="vox-ssb-field" value={editing.keywords} onChange={(e) => setEditing({ ...editing, keywords: e.target.value })} placeholder="Search keywords (space separated)" aria-label="Search keywords" />
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button type="button" className="coms-mini-btn tiny primary" disabled={busy} onClick={saveSound}>
                            Save
                          </button>
                          <button type="button" className="coms-mini-btn tiny" onClick={() => setEditing(null)}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="main">
                        <div className="name">
                          {s.emoji || '🔊'} {s.name}
                        </div>
                        <div className="meta">
                          {secs(s.duration_ms)}
                          {s.keywords && s.keywords.length ? ` · ${s.keywords.join(' ')}` : ''}
                          {s.description ? ` · ${s.description}` : ''}
                        </div>
                      </div>
                    )}
                    {!isEditing ? (
                      <div className="acts">
                        {can.manage ? (
                          <>
                            <button type="button" className="coms-mini-btn tiny" disabled={i === 0 || busy} onClick={() => move(c, i, -1)} aria-label={`Move ${s.name} up`}>
                              ↑
                            </button>
                            <button type="button" className="coms-mini-btn tiny" disabled={i === c.sounds.length - 1 || busy} onClick={() => move(c, i, 1)} aria-label={`Move ${s.name} down`}>
                              ↓
                            </button>
                          </>
                        ) : null}
                        {mine ? (
                          <>
                            <button type="button" className="coms-mini-btn tiny" onClick={() => setEditing({ id: s.id, name: s.name, emoji: s.emoji || '', description: s.description || '', keywords: (s.keywords || []).join(' ') })}>
                              Edit
                            </button>
                            <button type="button" className="coms-mini-btn tiny" onClick={() => { replacing.current = s.id; if (replaceRef.current) replaceRef.current.click(); }}>
                              Replace
                            </button>
                            {confirm === `s:${s.id}` ? (
                              <>
                                <button type="button" className="coms-mini-btn tiny vox-sbm-danger" disabled={busy} onClick={() => act(() => comsApiDelete(`/sounds/sounds/${s.id}`), 'Could not delete.', `Deleted “${s.name}”.`)}>
                                  Delete
                                </button>
                                <button type="button" className="coms-mini-btn tiny" onClick={() => setConfirm('')}>
                                  Keep
                                </button>
                              </>
                            ) : (
                              <button type="button" className="coms-mini-btn tiny" onClick={() => setConfirm(`s:${s.id}`)} aria-label={`Delete ${s.name}`}>
                                ✕
                              </button>
                            )}
                          </>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                );
              })
            ) : (
              <div className="vox-ssb-sub">No sounds yet.</div>
            )}
          </div>
        ))}
        {!collections.length ? <div className="vox-ssb-sub" style={{ marginTop: 10 }}>No sounds yet{can.upload ? ': drop an MP3 above to add the first one.' : '.'}</div> : null}
      </section>

      {/* ---------------- packs & sharing ---------------- */}
      {can.manage ? (
        <section className="vox-ssb-card" aria-labelledby="ssb-packs">
          <h3 id="ssb-packs">Packs and sharing</h3>
          <div className="vox-ssb-sub">The sound slots are shared by all of the server’s packs. Sharing never gives others the right to change your sounds.</div>
          {collections.map((c) => (
            <div key={c.id} className="vox-ssb-row" style={{ marginTop: 8 }}>
              <div className="main">
                <div className="name">{c.name}</div>
                <div className="meta">
                  {c.sound_count} sound{c.sound_count === 1 ? '' : 's'} · v{c.version}
                  {c.published ? ` · in the Marketplace · ${c.install_count} install${c.install_count === 1 ? '' : 's'}` : ''}
                </div>
              </div>
              <div className="acts" style={{ alignItems: 'center' }}>
                <select
                  className="vox-ssb-field"
                  aria-label={`Who may use ${c.name}`}
                  value={c.share_policy || 'local'}
                  disabled={busy}
                  onChange={(e) => {
                    const v = e.target.value;
                    act(() => comsApiPatch(`/sounds/collections/${c.id}`, { share_policy: v, visibility: v === 'anyone' ? 'unlisted' : 'server' }), 'Could not change sharing.', 'Sharing updated.');
                  }}
                >
                  {SHARE.map(([k, l]) => (
                    <option key={k} value={k}>
                      {l}
                    </option>
                  ))}
                </select>
                {c.share_policy === 'anyone' && c.share_code ? (
                  <button type="button" className="coms-mini-btn tiny" title={c.share_code} onClick={() => navigator.clipboard && navigator.clipboard.writeText(c.share_code).then(() => setNotice('Share code copied.')).catch(() => setNotice(`Share code: ${c.share_code}`))}>
                    Copy code
                  </button>
                ) : null}
                {c.share_policy === 'anyone' ? (
                  c.published ? (
                    <button type="button" className="coms-mini-btn tiny" onClick={() => act(() => comsApiPost(`/sounds/collections/${c.id}/unpublish`, {}), 'Could not unpublish.', 'Removed from the Marketplace.')}>
                      Unpublish
                    </button>
                  ) : (
                    <button type="button" className="coms-mini-btn tiny primary" disabled={!c.sound_count} onClick={() => act(() => comsApiPost(`/sounds/collections/${c.id}/publish`, { category: c.category || 'other' }), 'Could not publish.', 'Published in the Marketplace (free).')}>
                      Publish (free)
                    </button>
                  )
                ) : null}
                {collections.length > 1 ? (
                  confirm === `c:${c.id}` ? (
                    <>
                      <button type="button" className="coms-mini-btn tiny vox-sbm-danger" onClick={() => act(() => comsApiDelete(`/sounds/collections/${c.id}`), 'Could not delete the pack.', 'Pack deleted.')}>
                        Delete pack
                      </button>
                      <button type="button" className="coms-mini-btn tiny" onClick={() => setConfirm('')}>
                        Keep
                      </button>
                    </>
                  ) : (
                    <button type="button" className="coms-mini-btn tiny" onClick={() => setConfirm(`c:${c.id}`)} aria-label={`Delete pack ${c.name}`}>
                      ✕
                    </button>
                  )
                ) : null}
              </div>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
            <input className="vox-ssb-field" value={newPack} onChange={(e) => setNewPack(e.target.value)} placeholder="New pack name" aria-label="New pack name" maxLength={40} />
            <button type="button" className="coms-mini-btn" disabled={busy || newPack.trim().length < 2} onClick={() => act(async () => { await comsApiPost('/sounds/collections', { name: newPack.trim(), visibility: 'server', server_key: serverKey }); setNewPack(''); }, 'Could not create the pack.', 'Pack created.')}>
              Create pack
            </button>
          </div>

          <h3 style={{ marginTop: 16 }}>Shared packs enabled here</h3>
          <div className="vox-ssb-sub">Packs from creators or other servers that everyone here may play. They stay owned by their creators; nothing is copied.</div>
          {(lib.enabled_packs || []).map((p) => (
            <div key={p.id} className="vox-ssb-row" style={{ marginTop: 8 }}>
              <div className="main">
                <div className="name">{p.name}</div>
                <div className="meta">
                  {p.server_name ? `from ${p.server_name}` : `by ${p.creator || 'someone'}`} · {p.sound_count} sounds{p.still_shared ? '' : ' · no longer shared by its owner'}
                </div>
              </div>
              <button type="button" className="coms-mini-btn tiny" onClick={() => act(() => comsApiDelete(`/sounds/servers/${key}/packs/${p.id}`), 'Could not remove the pack.', 'Pack removed from this server.')}>
                Remove
              </button>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
            <input className="vox-ssb-field" value={packCode} onChange={(e) => setPackCode(e.target.value)} placeholder="Share code" aria-label="Pack share code" />
            <button type="button" className="coms-mini-btn" disabled={busy || !packCode.trim()} onClick={() => act(async () => { await comsApiPost(`/sounds/servers/${key}/packs`, { code: packCode.trim() }); setPackCode(''); }, 'Could not add that pack.', 'Pack enabled for this server.')}>
              Enable pack
            </button>
          </div>
        </section>
      ) : null}

      {/* ---------------- rules ---------------- */}
      {policy ? (
        <section className="vox-ssb-card" aria-labelledby="ssb-rules">
          <h3 id="ssb-rules">Soundboard rules</h3>
          {!canSettings ? <div className="vox-ssb-sub">Only members with the Manage soundboard settings permission can change these.</div> : null}
          <div className="vox-ssb-grid2">
            <label className="vox-ssb-toggle">
              <input type="checkbox" disabled={!canSettings} checked={!!settings.enabled} onChange={(e) => setPolicy({ enabled: e.target.checked })} data-testid="ssb-enabled" />
              <span>
                Soundboard enabled
                <small>Turn it off to silence it in every channel at once.</small>
              </span>
            </label>
            {toggle('allow_server_sounds', 'This server’s sounds')}
            {toggle('allow_personal_sounds', 'Members’ personal sounds', 'Their own sounds and clips (needs Use external sounds).')}
            {toggle('allow_other_server_sounds', 'Sounds from other servers', 'When that server lets its members use them elsewhere.')}
            {toggle('allow_marketplace_sounds', 'Installed Marketplace packs')}
            {toggle('approved_only', 'Only approved sounds', 'Only this server’s sounds and packs enabled here.')}
            {toggle('allow_interrupt', 'New sounds interrupt playing ones', 'Otherwise a sound waits until the channel is free.')}
            {toggle('moderators_can_stop', 'Moderators can stop sounds', 'Members who can mute others may stop sounds and pause a channel.')}
          </div>
          <div className="vox-ssb-grid2">
            <label className="vox-ssb-toggle" style={{ flexDirection: 'column', gap: 4 }}>
              <span>Cooldown per member: {(policy.user_cooldown_ms / 1000).toFixed(1)} s</span>
              <input type="range" min={0} max={30000} step={500} disabled={!canSettings} value={policy.user_cooldown_ms} aria-label="Cooldown per member" onChange={(e) => setPolicyNumber('user_cooldown_ms', Number(e.target.value))} />
            </label>
            <label className="vox-ssb-toggle" style={{ flexDirection: 'column', gap: 4 }}>
              <span>Cooldown per channel: {(policy.channel_cooldown_ms / 1000).toFixed(1)} s</span>
              <input type="range" min={0} max={30000} step={500} disabled={!canSettings} value={policy.channel_cooldown_ms} aria-label="Cooldown per channel" onChange={(e) => setPolicyNumber('channel_cooldown_ms', Number(e.target.value))} />
            </label>
            <label className="vox-ssb-toggle" style={{ flexDirection: 'column', gap: 4 }}>
              <span>Sounds at the same time: {policy.max_concurrent}</span>
              <input type="range" min={1} max={5} step={1} disabled={!canSettings} value={policy.max_concurrent} aria-label="Sounds at the same time" onChange={(e) => setPolicyNumber('max_concurrent', Number(e.target.value))} />
            </label>
            <label className="vox-ssb-toggle" style={{ flexDirection: 'column', gap: 4 }}>
              <span>Maximum volume: {Math.round(policy.max_volume * 100)}%</span>
              <input type="range" min={0.1} max={1} step={0.05} disabled={!canSettings} value={policy.max_volume} aria-label="Maximum volume" onChange={(e) => setPolicyNumber('max_volume', Number(e.target.value))} />
            </label>
          </div>

          {(settings.channels || []).length ? (
            <>
              <h3 style={{ marginTop: 16 }}>Voice channels</h3>
              {(settings.channels || []).map((ch) => (
                <div key={ch.id} className="vox-ssb-row" style={{ marginTop: 6 }}>
                  <div className="main">
                    <div className="name">🔊 {ch.name}</div>
                    <div className="meta">{ch.paused ? (ch.until ? `Paused until ${new Date(ch.until).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : 'Soundboard off in this channel') : 'Soundboard on'}</div>
                  </div>
                  {canStop ? (
                    <div className="acts">
                      {ch.paused ? (
                        <button type="button" className="coms-mini-btn tiny" onClick={() => act(() => comsApiPut(`/sounds/server-settings/${key}/channels/${ch.id}`, { paused: false }), 'Could not change the channel.')}>
                          Turn on
                        </button>
                      ) : (
                        <>
                          <button type="button" className="coms-mini-btn tiny" onClick={() => act(() => comsApiPut(`/sounds/server-settings/${key}/channels/${ch.id}`, { paused: true, minutes: 15 }), 'Could not pause the channel.')}>
                            Pause 15 min
                          </button>
                          <button type="button" className="coms-mini-btn tiny" onClick={() => act(() => comsApiPut(`/sounds/server-settings/${key}/channels/${ch.id}`, { paused: true }), 'Could not change the channel.')}>
                            Turn off here
                          </button>
                        </>
                      )}
                    </div>
                  ) : null}
                </div>
              ))}
            </>
          ) : null}
        </section>
      ) : null}

      {/* ---------------- roles ---------------- */}
      {roleRows.length ? (
        <section className="vox-ssb-card" aria-labelledby="ssb-roles">
          <h3 id="ssb-roles">Who can do what</h3>
          <div className="vox-ssb-sub">Soundboard permissions of each role (the same switches as in Roles). {canRoles ? 'You can change roles below your own highest role.' : 'Changing them needs the Manage roles permission.'}</div>
          <div className="vox-ssb-scroll">
            <table className="vox-ssb-table">
              <thead>
                <tr>
                  <th scope="col">Role</th>
                  {SB_PERMS.map(([k, l]) => (
                    <th key={k} scope="col">
                      {l}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {roleRows.map((r) => (
                  <tr key={r.id}>
                    <td style={{ color: r.color || undefined, fontWeight: 800 }}>{r.name}</td>
                    {SB_PERMS.map(([k, l]) => (
                      <td key={k}>
                        <input type="checkbox" aria-label={`${l} for ${r.name}`} checked={Number(r[k]) === 1} disabled={!canRoles || !r.can_manage || !grantable.has(k)} onChange={(e) => setRolePerm(r, k, e.target.checked)} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
    </div>
  );
}
